/**
 * hooks/useDepositNotifications.ts
 * Periodically processes due deposits and expenses, and shows toast notifications.
 * Hardened: re-entrancy guard + deferred first run to avoid nested updates on mount.
 */

import { useEffect, useRef } from 'react'
import { useAccountingStore } from '../store/accounting'
import { toast } from 'sonner'
import { formatCurrency } from '../lib/format'

/**
 * useDepositNotifications
 * Checks for due deposits and expenses on mount and every 60s.
 * - Uses getState() to avoid selector resubscriptions.
 * - Guards against overlapping runs to prevent nested updates.
 */
export function useDepositNotifications() {
  const runningRef = useRef(false)

  useEffect(() => {
    /**
     * check
     * Triggers processing of due deposits/expenses and shows toasts.
     * Guarded to avoid re-entrancy and nested updates.
     */
    const check = () => {
      if (runningRef.current) return
      runningRef.current = true
      try {
        const deposited = useAccountingStore.getState().processDueDeposits()
        const paid = useAccountingStore.getState().processDueExpenses()

        if (deposited.length > 0) {
          if (deposited.length === 1) {
            const t = deposited[0]
            toast.success(`تم إيداع ${formatCurrency(t.netAmount)} (${t.gateway}) إلى الحساب الجاري`)
          } else {
            const total = deposited.reduce((s, x) => s + x.netAmount, 0)
            toast.success(`تم إيداع ${deposited.length} عمليات بإجمالي ${formatCurrency(total)} إلى الحساب الجاري`)
          }
        }

        if (paid.length > 0) {
          if (paid.length === 1) {
            const e = paid[0]
            toast.warning(`تم دفع مصروف ${e.category}: ${formatCurrency(e.amount)} من الحساب الجاري`)
          } else {
            const total = paid.reduce((s, x) => s + x.amount, 0)
            toast.warning(`تم دفع ${paid.length} مصروفات بإجمالي ${formatCurrency(total)} من الحساب الجاري`)
          }
        }
      } finally {
        runningRef.current = false
      }
    }

    // Defer initial run to the next macrotask to avoid nested updates during mount commit
    const immediate = setTimeout(check, 0)
    const id = setInterval(check, 60_000)
    return () => {
      clearTimeout(immediate)
      clearInterval(id)
    }
  }, [])
}
